import {JsonApiResponse} from '@/types/JsonApiResponse.interface';
import {JsonApiResponseSchema} from '@/schema/JsonApiResponse.schema';
import {PromocodesServiceInterface} from './PromocodesService.interface';
import {PromocodesServiceSchema} from './PromocodesService.schema';
import {API} from '@/api/API';
import {postData} from '@/utils/sendData';
import JSONAPI from '@/utils/JSONAPI/JSONAPI';
import {JsonApiQueryParamsInterface} from '@/types/JsonApiQueryParams.interface';
import camelcaseKeys from "camelcase-keys";

export const getPromocodesResources = async (params?: JsonApiQueryParamsInterface): Promise<JsonApiResponse<PromocodesServiceInterface[]>> => {
    const fallback: JsonApiResponse<PromocodesServiceInterface[]> = {
        data: [],
        meta: JsonApiResponseSchema.meta,
    };

    const url = API.getPromocodeList.path(camelcaseKeys(params, {deep: true}));
    const method = API.getPromocodeList.method;

    try {
        const response = await postData(null, url, method, null);
        const data = await JSONAPI.deserialize<PromocodesServiceInterface[]>(response.data);
        const meta = {...fallback.meta, ...(response.data.meta?.page ?? {})};
        const result = camelcaseKeys({...fallback, data, meta}, {deep: true});

        return result as JsonApiResponse<PromocodesServiceInterface[]>;
    } catch (e) {
        console.error('[getPromocodesResources]', e);
        return fallback;
    }
};

export const getPromocodesResource = async (id, params?: JsonApiQueryParamsInterface): Promise<JsonApiResponse<PromocodesServiceInterface>> => {
    const fallback: JsonApiResponse<PromocodesServiceInterface> = {
        data: {...PromocodesServiceSchema},
        meta: JsonApiResponseSchema.meta,
    };

    const url = API.getPromocode.path(id, params);
    const method = API.getPromocode.method;

    try {
        const response = await postData(null, url, method, null);
        const data = await JSONAPI.deserialize<PromocodesServiceInterface>(response.data);
        const meta = {...fallback.meta, ...(response.data.meta?.page ?? {})};
        const result = camelcaseKeys({...fallback, data, meta}, {deep: true});

        return result as JsonApiResponse<PromocodesServiceInterface>;
    } catch (e) {
        console.error('[getPromocodesResource]', e);
        return fallback;
    }
};

export const createPromocodesResource = async (params?: JsonApiQueryParamsInterface): Promise<JsonApiResponse<PromocodesServiceInterface>> => {
    const fallback: JsonApiResponse<PromocodesServiceInterface> = {
        data: {...PromocodesServiceSchema},
        meta: JsonApiResponseSchema.meta,
    };

    const url = API.createPromocode.path
    const method = API.createPromocode.method

    try {
        const response = await postData(null, url, method, null);
        const data = await JSONAPI.deserialize<PromocodesServiceInterface>(response.data);
        const meta = {...fallback.meta, ...(response.data.meta?.page ?? {})};
        const result = camelcaseKeys({...fallback, data, meta}, {deep: true});

        return result as JsonApiResponse<PromocodesServiceInterface>;
    } catch (e) {
        console.error('[createPromocodesResource]', e);
        return fallback;
    }
};

export const updatePromocodesResource = async (params?: JsonApiQueryParamsInterface): Promise<JsonApiResponse<PromocodesServiceInterface>> => {
    const fallback: JsonApiResponse<PromocodesServiceInterface> = {
        data: {...PromocodesServiceSchema},
        meta: JsonApiResponseSchema.meta,
    };

    const url = API.updatePromocode.path(params)
    const method = API.updatePromocode.method

    try {
        const response = await postData(null, url, method, null);
        const data = await JSONAPI.deserialize<PromocodesServiceInterface>(response.data);
        const meta = {...fallback.meta, ...(response.data.meta?.page ?? {})};
        const result = camelcaseKeys({...fallback, data, meta}, {deep: true});

        return result as JsonApiResponse<PromocodesServiceInterface>;
    } catch (e) {
        console.error('[updatePromocodesResource]', e);
        return fallback;
    }
};

export const deletePromocodesResource = async (id: number): Promise<JsonApiResponse<PromocodesServiceInterface>> => {
    const fallback: JsonApiResponse<PromocodesServiceInterface> = {
        data: {...PromocodesServiceSchema},
        meta: JsonApiResponseSchema.meta,
    };

    try {
        const url = API.deletePromocode.path(id);
        const method = API.deletePromocode.method;

        const response = await postData(null, url, method, null);
        const data = await JSONAPI.deserialize<PromocodesServiceInterface>(response.data);
        const meta = {...fallback.meta, ...(response.data.meta?.page ?? {})};

        return camelcaseKeys({data, meta}, {deep: true}) as JsonApiResponse<PromocodesServiceInterface>;
    } catch (e) {
        console.error('[deletePromocodesResource]', e);
        return fallback;
    }
};

export const exportPromocodesToExcel = async (params?: Record<string, any>): Promise<any> => {
    try {
        const url = API.exportPromocodesToExcel.path;
        const method = API.exportPromocodesToExcel.method;
        return await postData(params, url, method, null);
    } catch (e: any) {
        console.error('[importPromocodesToExcel]', e?.response ?? e);
        return e?.response;
    }
};

export const importPromocodesToExcel = async (formData: FormData): Promise<any> => {
    try {
        const url = API.importPromocodesToExcel.path;
        const method = API.importPromocodesToExcel.method;
        return await postData(formData, url, method, null);
    } catch (e: any) {
        console.error('[importPromocodesToExcel]', e?.response ?? e);
        return e?.response;
    }
};

export const templatePromocodesToExcel = async (): Promise<any> => {
    try {
        const url = API.templatePromocodesToExcel.path;
        const method = API.templatePromocodesToExcel.method;
        return await postData({}, url, method, null);
    } catch (e: any) {
        console.error('[templatePromocodesToExcel]', e?.response ?? e);
        return e?.response;
    }
};

export const attachEventToPromocode = async (eventId: number, promocodeId: number) => {
    try {
        const url = API.attachEventToPromocode.path(promocodeId, eventId);
        const method = API.attachEventToPromocode.method
        return await postData({}, url, method, null);
    } catch (e: any) {
        console.error('[attachEventToPromocode]', e?.response ?? e);
        return e?.response;
    }
}

export const detachEventFromPromocode = async (eventId: number, promocodeId: number) => {
    try {
        const url = API.detachEventFromPromocode.path(promocodeId, eventId);
        const method = API.detachEventFromPromocode.method;
        return await postData({}, url, method, null);
    } catch (e: any) {
        console.error('[attachEventToPromocode]', e?.response ?? e);
        return e?.response;
    }
}

export const attachTicketToPromocode = async (eventId: number, ticketId: number) => {
    try {
        const url = API.attachTicketToPromocode.path(ticketId, eventId);
        const method = API.attachTicketToPromocode.method;
        return await postData({}, url, method, null);
    } catch (e: any) {
        console.error('[attachEventToPromocode]', e?.response ?? e);
        return e?.response;
    }
}

export const detachTicketFromPromocode = async (eventId: number, ticketId: number) => {
    try {
        const url = API.detachTicketFromPromocode.path(ticketId, eventId);
        const method = API.detachTicketFromPromocode.method;
        return await postData({}, url, method, null);
    } catch (e: any) {
        console.error('[attachEventToPromocode]', e?.response ?? e);
        return e?.response;
    }
}
